import { Box, Stack } from '@mui/material'
import {
	getDaysBetweenDates,
	getFutureDates,
} from 'components/shared/processes'
import { FC, useCallback, useEffect, useMemo, useState } from 'react'
import DateRangePicker from 'ui/DatePickerInput/DateRangePicker'
import BreadCrumbsGroup from 'ui/breadCrumbs/BreadCrumbsGroup'
import { useUnavailableShiftsForParlors } from 'hook/query/useParlorAvailableShiftPeriods'
import { useMilkingParlorFilters } from '../../../hooks/useMilkingParlorFilters'
import { useShiftFilters } from '../../../hooks/useShiftFilters'

type MilkingShiftsHeaderProps = {
	onFiltersChange?: (filters: {
		shift: ReturnType<typeof useShiftFilters>['shift']
		parlorId: ReturnType<typeof useMilkingParlorFilters>['parlorId']
		dateRange: { startDate: number; endDate: number }
		daysBetweenDates: number
	}) => void
}

export const MilkingShiftsHeader: FC<MilkingShiftsHeaderProps> = ({
	onFiltersChange,
}) => {
	const {
		handleToggleParlor,
		handleRemoveParlor,
		availableParlors,
		parlorBreadcrumbs,
		parlorId,
	} = useMilkingParlorFilters()

	const unavailableShifts = useUnavailableShiftsForParlors(parlorId)

	const {
		handleToggleShift,
		handleRemoveShift,
		availableShifts,
		shiftBreadcrumbs,
		shift,
	} = useShiftFilters(unavailableShifts)

	const [dateRange, setDateRange] = useState(getFutureDates(6))

	const onChangeCallback = useCallback((value: number[]) => {
		setDateRange({ startDate: value[0], endDate: value[1] })
	}, [])

	const daysBetweenDates = useMemo(
		() => getDaysBetweenDates(dateRange),
		[dateRange],
	)

	useEffect(() => {
		onFiltersChange?.({
			shift,
			parlorId,
			dateRange,
			daysBetweenDates,
		})
	}, [shift, parlorId, dateRange, daysBetweenDates, onFiltersChange])

	return (
		<Stack
			direction='row'
			justifyContent='space-between'
			alignItems='end'
			gap='24px'
			sx={{ minWidth: 0, width: '100%', flexWrap: 'nowrap' }}
		>
			<Stack direction='column' gap='12px' sx={{ minWidth: 0, flex: 1 }}>
				{availableParlors.length > 1 && (
					<BreadCrumbsGroup
						handleActiveFilter={handleToggleParlor}
						handleRemoveFilter={handleRemoveParlor}
						array={parlorBreadcrumbs}
					/>
				)}
				{availableShifts.length > 0 && (
					<BreadCrumbsGroup
						handleActiveFilter={handleToggleShift}
						handleRemoveFilter={handleRemoveShift}
						array={shiftBreadcrumbs}
					/>
				)}
			</Stack>
			<Box sx={{ flexShrink: 0 }}>
				<DateRangePicker
					onChangeUpdateDate={onChangeCallback}
					startDate={dateRange.startDate}
					endDate={dateRange.endDate}
					isShowResetBtn={true}
					resetButtonPosition='left'
				/>
			</Box>
		</Stack>
	)
}
